import { useState, useEffect, useCallback } from 'react';
import { getRemovedProfiles, activateProfile } from '../../services/businessService';
import type { BusinessProfile } from '../../types/business';
import { ConfirmProfileActionModal } from './ConfirmProfileActionModal';

interface RemovedProfilesModalProps {
  businessId: string;
  title?: string;
  contextLabel?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function RemovedProfilesModal({
  businessId,
  title = 'Removed Users',
  contextLabel = 'team',
  onClose,
  onSuccess,
}: RemovedProfilesModalProps) {
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activateTarget, setActivateTarget] = useState<BusinessProfile | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRemoved = useCallback(async () => {
    setLoading(true);
    try {
      const removed = await getRemovedProfiles(businessId);
      setProfiles(removed);
    } catch {
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { fetchRemoved(); }, [fetchRemoved]);

  const handleActivate = async () => {
    if (!activateTarget) return;
    setActionLoading(true);
    try {
      await activateProfile(businessId, activateTarget.id);
      setActivateTarget(null);
      onSuccess();
      fetchRemoved();
    } catch {
      // error handled by apiService
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{title}</h2>
          {loading ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : profiles.length > 0 ? (
            profiles.map((profile) => (
              <div key={profile.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{profile.displayName}</p>
                  <p className="text-sm text-gray-500 truncate">{profile.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActivateTarget(profile)}
                  className="shrink-0 px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Activate
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No removed users.</p>
          )}
        </div>
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {activateTarget && (
        <ConfirmProfileActionModal
          action="activate"
          displayName={activateTarget.displayName}
          contextLabel={contextLabel}
          loading={actionLoading}
          onClose={() => setActivateTarget(null)}
          onConfirm={handleActivate}
        />
      )}
    </div>
  );
}
